import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SubHeader from "../../custom/sub-header/sub-header";
import useAuth from "../../../hooks/useAuth";
import ReportService from "../../../service/report-service";
import { LINE_URL } from "../../../constant/contact";
import "./account-page-style.scss";

//帳號中心
//
//  進入點：底部頁籤「我的」、首頁右上角漢堡。
//  已被 RequireUserAuth 包住，所以這裡的 user 一定存在。
//
//  版面是一串功能列：歡迎列（→ 個人檔案）、宣傳橫幅、報告收藏庫、優惠券、客服中心。
const AccountPage = () => {
  const { user } = useAuth();
  //報告數量：載入前用 null 表示「還不知道」，才能跟「真的是 0」區分開
  const [reportCount, setReportCount] = useState(null);

  useEffect(() => {
    //  數字要跟收藏庫裡實際看到的本數一致，所以讀 R-4 而不是 R-3。
    //  R-3 是照 report.userID 撈的 —— 報告類商品不用登入就能買，
    //  那些報告的 userID 是 guest:<orderID>，所以它回的 total
    //  對「先用訪客買、之後才註冊」的人永遠是 0。
    //  （R-4 從訂單撈，userID 或 email 兩者取其一。）
    ReportService.library()
      .then((res) => setReportCount(res.data?.data?.total ?? 0))
      .catch(() => setReportCount(0)); //取不到就顯示 0，不要讓整頁壞掉
  }, []);

  return (
    <main className="acc">
      <SubHeader />

      <p className="acc__section-label">帳號中心</p>

      {/* 歡迎列：點進去是「個人檔案」（個人資料、登出、註銷） */}
      <Link to="/my-page" className="acc__row acc__row--welcome">
        <IconUser />
        <span className="acc__row-title">
          歡迎，{user?.userName || "朋友"}！
        </span>
        <IconChevron />
      </Link>

      {/* 宣傳橫幅：導去問卦（伏爻子）。
          配色與用語都跟著那一頁走 —— 原本是緋紅那套紅底配
          「再來問 3 個問題」，但 /ask 是伏爻子的問卦（寫問題 → 搖龜殼 →
          斷語，一事一卦）。「問 3 個問題」是白夜提問（/question）的說法，
          點進去會發現跟橫幅上寫的不是同一件事。 */}
      <Link
        to="/ask"
        className="acc__banner"
        style={{ "--art": `url(${process.env.PUBLIC_URL}/images/characters/fuyaozi-wide.jpg)` }}
      >
        <span className="acc__banner-glyph" aria-hidden="true">卦</span>
        <span className="acc__banner-text">
          心裡擱著一件事吧？
          <br />
          <strong>搖一卦</strong>，我幫你看看。
        </span>
        <IconChevron />
      </Link>

      <Link to="/my-report" className="acc__row">
        <IconBox />
        <span className="acc__row-title">報告收藏庫</span>
        {/* 還在載入時不顯示數字，避免先閃一個 0 再跳成真實值 */}
        {reportCount !== null && (
          <span className="acc__row-count">{reportCount}</span>
        )}
        <IconChevron />
      </Link>

      {/* 頁面已經做好了（pages/coupon），缺的只有後端 ——
          優惠券還沒有模型，所以清單與這個數字都先固定是 0。
          做券系統時：把 constant/coupon-content 的 COUPONS 換成 API 回來的
          清單，這裡的 0 換成它的長度，兩邊的版型都不用動。 */}
      <Link to="/coupon" className="acc__row">
        <IconTag />
        <span className="acc__row-title">優惠券</span>
        <span className="acc__row-count">0</span>
        <IconChevron />
      </Link>

      <div className="acc__divider" />

      {/* 跟導覽列那顆一樣開 LINE，不走 /support（那條 route 不存在）。
          用 <a> 不是 <Link>：站外連結交給瀏覽器，不要進 react-router。 */}
      <a
        href={LINE_URL}
        target="_blank"
        rel="noreferrer"
        className="acc__row"
      >
        <IconChat />
        <span className="acc__row-title">官方 LINE 客服</span>
        <IconChevron />
      </a>
    </main>
  );
};

//—— 圖示 ——
const IconChevron = () => (
  <svg className="acc__chevron" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconUser = () => (
  <svg className="acc__icon" viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
    <circle cx="12" cy="8.5" r="3.7" stroke="currentColor" strokeWidth="1.7" fill="none" />
    <path d="M5 20a7 7 0 0114 0" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinecap="round" />
  </svg>
);

const IconBox = () => (
  <svg className="acc__icon" viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
    <path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2z" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" />
    <path d="M4 7.2l8 4.3 8-4.3M12 11.5V21" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" />
  </svg>
);

const IconTag = () => (
  <svg className="acc__icon" viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
    <path d="M3.5 11.3V4.5a1 1 0 011-1h6.8a1 1 0 01.7.3l8.2 8.2a1 1 0 010 1.4l-6.8 6.8a1 1 0 01-1.4 0L3.8 12a1 1 0 01-.3-.7z" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" />
    <circle cx="7.8" cy="7.8" r="1.4" fill="currentColor" />
  </svg>
);

const IconChat = () => (
  <svg className="acc__icon" viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
    <path d="M20 14.5a3 3 0 01-3 3H9.5L5 21v-3.5a3 3 0 01-1-2.24V7.5a3 3 0 013-3h10a3 3 0 013 3z" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" />
  </svg>
);

export default AccountPage;
